// src/hooks/usePausedDataLoader.js

import { useEffect, useState } from 'react';

import client from '../../../../utils/axiosInstance';
import {
  isBipolarSlider,
  isCheckbox,
  isDate,
  isENPSPromoter,
  isRadio,
  isRadioFace,
  isRange,
  isSlider,
} from '../../utils/questionTypeUtils';


/**
 * Hook personalizado para cargar y restaurar datos pausados de una encuesta
 * @param {Object} params - Parámetros del hook
 * @param {Object} params.data - Data con answerId
 * @param {Array} params.questions - Array de preguntas
 * @param {Function} params.mapPausedDataToFormValues - Función para mapear datos pausados
 * @param {Function} params.recomputeVisibilityFromAnswers - Función para recomputar visibilidad
 * @param {Function} params.setFormValues - Setter para formValues
 * @param {Function} params.setOtherOptionValues - Setter para otherOptionValues
 * @param {Function} params.setSelectedRadioOptions - Setter para selectedRadioOptions
 * @param {Function} params.setValues - Setter para values
 * @returns {Object} Estado de carga
 */
export const usePausedDataLoader = ({
  data,
  questions,
  mapPausedDataToFormValues,
  recomputeVisibilityFromAnswers,
  setFormValues,
  setOtherOptionValues,
  setSelectedRadioOptions,
  setValues,
}) => {
  const [isPausedDataLoaded, setIsPausedDataLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    const fetchPausedData = async () => {
      // ✅ Early returns para casos donde no necesitamos hacer nada
      if (!data?.answerId || questions.length === 0) {
        return;
      }

      if (isPausedDataLoaded) {
        return;
      }

      // ✅ Si no hay answerId pero hay questions, marcar como cargado
      if (data?.answerId === undefined && questions.length > 0) {
        setIsPausedDataLoaded(true);
        return;
      }

      try {
        // 1️⃣ Cargar datos pausados desde API
        const response = await client.get(
          `GetPausedBySession/${data.answerId}`
        );
        const pausedData = response.data;

        // 2️⃣ Restaurar formValues
        const restoredFormValues = mapPausedDataToFormValues(pausedData);

        // 3️⃣ Extraer estados adicionales
        const restoredStates = extractRestoredStates(pausedData, questions);

        // 4️⃣ Aplicar TODOS los estados
        setFormValues(restoredFormValues);
        setOtherOptionValues(restoredStates.otherValues);
        setSelectedRadioOptions(restoredStates.radioSelections);
        setValues(restoredStates.sliderValues);

        // 5️⃣ Recomputar visibilidad después de un delay
        setTimeout(() => {
          recomputeVisibilityFromAnswers(restoredFormValues);
          setIsPausedDataLoaded(true);
        }, 100);
      } catch (error) {
        console.error('❌ Error loading paused data:', error);
        setLoadError(error);
        setIsPausedDataLoaded(true);
      }
    };

    fetchPausedData();
  }, [data?.answerId, questions.length, isPausedDataLoaded]);

  return {
    isPausedDataLoaded,
    loadError,
  };
};

/**
 * Extrae y organiza los estados restaurados desde pausedData
 * @param {Object} pausedData - Datos pausados de la API
 * @param {Array} questions - Array de preguntas
 * @returns {Object} Estados restaurados
 */
const extractRestoredStates = (pausedData, questions) => {
  const restoredOtherValues = {};
  const restoredRadioSelections = {};
  const restoredSliderValues = {};

  pausedData.answers.forEach((answer) => {
    const question = questions.find((q) => q.questionId === answer.id);
    if (!question) return;

    const questionId = question.questionId;
    const questionIndex = questions.findIndex((q) => q.questionId === answer.id);

    // Restaurar diferentes tipos de preguntas
    if (isRadio(question.typeQuestion) || isRadioFace(question.typeQuestion)) {
      restoreRadioValues(answer, question, questionId, restoredRadioSelections, restoredOtherValues);
    }

    if (isCheckbox(question.typeQuestion) && answer.values) {
      restoreCheckboxValues(answer, question, questionId, restoredOtherValues);
    }

    if (
      isSlider(question.typeQuestion) ||
      isENPSPromoter(question.typeQuestion) ||
      isBipolarSlider(question.typeQuestion) ||
      isRange(question.typeQuestion)
    ) {
      restoreSliderValues(answer, questionIndex, restoredSliderValues);
    }

    if (isDate(question.typeQuestion) && answer.value) {
      restoredSliderValues[questionIndex] = answer.value;
    }
  });

  return {
    otherValues: restoredOtherValues,
    radioSelections: restoredRadioSelections,
    sliderValues: restoredSliderValues,
  };
};

/**
 * Restaura valores de radio buttons
 */
const restoreRadioValues = (
  answer,
  question,
  questionId,
  radioSelections,
  otherValues
) => {
  if (!answer.value) return;

  radioSelections[questionId] = answer.value;

  const selectedOption = question.options.find(
    (opt) => opt.optionName === answer.value
  );

  // Si no encuentra la opción y hay opción "Otro", es un valor personalizado
  if (!selectedOption && question.options.some((opt) => opt.isOther)) {
    otherValues[questionId] = answer.value;
  }
};

/**
 * Restaura valores de checkboxes (incluyendo campo "Otro")
 */
const restoreCheckboxValues = (answer, question, questionId, otherValues) => {
  Object.keys(answer.values).forEach((key) => {
    const isOriginalOption = question.options.some(
      (opt) => opt.optionName === key
    );

    // Si no es una opción original, es el valor del campo "Otro"
    if (!isOriginalOption && answer.values[key]) {
      otherValues[questionId] = key;
    }
  });
};

/**
 * Restaura valores de sliders y escalas
 */
const restoreSliderValues = (answer, questionIndex, sliderValues) => {
  if (answer.value && answer.value !== '') {
    const numValue = Number(answer.value);
    if (!isNaN(numValue)) {
      sliderValues[questionIndex] = numValue;
    }
  }
};